// Comprehensive site knowledge base for Francis Odame's website
export const siteKnowledge = {
  // Author Information
  author: {
    name: "Francis Odame",
    title: "Author • Health Advocate • Community Voice",
    description: "A passionate writer and dedicated healthcare professional whose mission is to inspire, educate, and empower people through words and wellness.",
    background: "Francis Odame is an author, health advocate, and community voice dedicated to healing through words. He combines storytelling with practical health strategies to inspire positive change in communities and individual lives.",
    expertise: ["Author & Writer", "Health Advocate", "Community Specialist"],
    mission: "To inspire healthier, purpose-driven lives through the power of words and knowledge",
    vision: "A world where literature, wellness, and community come together to nurture stronger, healthier, and more inspired generations"
  },

  // Books Information
  books: {
    main: {
      title: "Because of Abigail",
      description: "A heartfelt story of love and loss that inspires resilience and hope. This powerful narrative explores themes of healing, growth, and the transformative power of relationships.",
      genre: "Inspirational Fiction",
      themes: ["Love", "Loss", "Healing", "Resilience", "Hope", "Transformation"],
      purchaseLink: "https://amzn.eu/d/7zSqF9E"
    },
    platforms: [
      {
        name: "Waterstones",
        link: "https://www.waterstones.com/book/because-of-abigail/francis-odame/9781834187440",
        description: "Purchase with fast UK delivery and support for local bookstores"
      },
      {
        name: "Hatchards", 
        link: "https://www.hatchards.co.uk/book/because-of-abigail/francis-odame/9781834187457",
        description: "Order from London's oldest bookshop since 1797"
      },
      {
        name: "Google Books",
        link: "https://books.google.co.uk/books/about/Because_of_Abigail.html?id=dVmQ0QEACAAJ&redir_esc=y",
        description: "Read previews and purchase digital copies"
      },
      {
        name: "Barnes & Noble",
        link: "https://www.barnesandnoble.com/w/because-of-abigail-francis-odame/1148203351",
        description: "Buy from America's largest bookstore chain"
      }
    ]
  },

  // Speaking Services
  speaking: {
    services: [
      {
        type: "School Assembly",
        description: "Interactive presentations for students on mental health, resilience, and personal growth",
        features: ["Age-appropriate content", "Interactive Q&A", "Follow-up resources"],
        audience: "Students and educational staff"
      },
      {
        type: "Church Event", 
        description: "Inspirational talks combining faith, wellness, and community building",
        features: ["Faith-based approach", "Community focus", "Spiritual wellness"],
        audience: "Church congregations and faith communities"
      },
      {
        type: "Community Workshop",
        description: "Hands-on workshops for community groups focused on health and wellness",
        features: ["Interactive sessions", "Practical tools", "Group activities"],
        audience: "Community organizations and groups"
      },
      {
        type: "Corporate Event",
        description: "Professional wellness and resilience programs for corporate audiences",
        features: ["Workplace wellness", "Team building", "Stress management"],
        audience: "Corporate teams and organizations"
      }
    ],
    topics: [
      "Mental health awareness and stigma reduction",
      "Building resilience in challenging times", 
      "Community wellness and connection",
      "Youth empowerment and development",
      "Faith-based wellness and healing",
      "Practical health strategies for everyday life"
    ],
    booking: {
      phone: "+44 7380 242303",
      whatsapp: "+44 7380 242303", 
      email: "francis@francisodame.com",
      process: "Contact via WhatsApp for quick booking, email for detailed inquiries, or use the contact form on the website"
    }
  },

  // Website Navigation
  navigation: {
    pages: [
      {
        name: "Home",
        path: "/",
        description: "Main landing page with Francis Odame's hero story, book information, and speaking services overview"
      },
      {
        name: "About", 
        path: "/about",
        description: "Francis Odame's personal story, mission, values, and professional background"
      },
      {
        name: "Books",
        path: "/books", 
        description: "Book listings, purchase options, and platform information for 'Because of Abigail'"
      },
      {
        name: "Speaking",
        path: "/speaking",
        description: "Speaking services, topics, booking information, and speaking style details"
      },
      {
        name: "Blog",
        path: "/blog",
        description: "Articles and resources on health, wellness, and community topics"
      },
      {
        name: "Testimonials", 
        path: "/testimonials",
        description: "Feedback and reviews from people who have worked with Francis Odame"
      },
      {
        name: "Contact",
        path: "/contact", 
        description: "Multiple ways to reach Francis Odame including WhatsApp, email, phone, and booking forms"
      }
    ]
  },

  // Contact Information
  contact: {
    email: "info@francisodame.com",
    phone: "+44 7380 242303", 
    whatsapp: "+44 7380 242303",
    social: {
      facebook: "https://www.facebook.com/share/1AS7TuWt2U/?mibextid=wwXIfr",
        instagram: "https://www.instagram.com/fodamewrites?igsh=MWF5OHQ4eDlzeHZ2aw%3D%3D&utm_source=qr",
      linkedin: "https://www.linkedin.com/in/francis-odame-2833a7216?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      tiktok: "https://www.tiktok.com/@fodamewrites?_t=ZN-907HCZMxm6w&_r=1"
    }
  },

  // Values and Approach
  values: {
    inspiration: "Sparking motivation and self-belief through powerful storytelling and real-world examples",
    education: "Simplifying health and life lessons for everyday people, making complex concepts accessible", 
    connection: "Engaging audiences through relatable stories and real-life experiences that resonate deeply"
  }
};

// Smart query functions
export const querySiteKnowledge = (userMessage) => {
  const message = userMessage.toLowerCase();
  
  // Navigation queries
  if (message.includes('navigate') || message.includes('go to') || message.includes('visit') || message.includes('find')) {
    return handleNavigationQuery(message);
  }
  
  // Author information queries
  if (message.includes('who is') || message.includes('about francis') || message.includes('author') || message.includes('francis odame') || message.includes('background') || message.includes('story') || message.includes('tell me about')) {
    return handleAuthorQuery(message);
  }
  
  // Book queries
  if (message.includes('book') || message.includes('because of abigail') || message.includes('purchase') || message.includes('buy') || message.includes('read')) {
    return handleBookQuery(message);
  }
  
  // Speaking services queries
  if (message.includes('speaking') || message.includes('speak') || message.includes('workshop') || message.includes('book') || message.includes('presentation')) {
    return handleSpeakingQuery(message);
  }
  
  // Contact queries
  if (message.includes('contact') || message.includes('reach') || message.includes('email') || message.includes('phone') || message.includes('whatsapp')) {
    return handleContactQuery(message);
  }
  
  // Mission and values queries
  if (message.includes('mission') || message.includes('purpose') || message.includes('goal') || message.includes('vision') || message.includes('values')) {
    return handleMissionQuery(message);
  }
  
  // Blog queries
  if (message.includes('blog') || message.includes('article') || message.includes('write') || message.includes('content')) {
    return handleBlogQuery(message);
  }
  
  // Testimonials queries
  if (message.includes('testimonial') || message.includes('review') || message.includes('feedback') || message.includes('experience')) {
    return handleTestimonialQuery(message);
  }
  
  // General help
  if (message.includes('help') || message.includes('what can') || message.includes('assist') || message.includes('support') || message.includes('info') || message.includes('information')) {
    return handleHelpQuery();
  }
  
  // Default response for unrelated queries
  if (isUnrelatedQuery(message)) {
    return "I'm specialized to help you with information about Francis Odame and this website only. I can assist you with questions about the author, his books, speaking services, or help you navigate this website. How can I help you learn more about Francis Odame's work?";
  }
  
  // Default response - try to be more helpful
  return "I'd be happy to help you learn more about Francis Odame and this website! You can ask me about his background, books, speaking services, or how to navigate to different pages. What would you like to know?";
};

// Navigation query handler
const handleNavigationQuery = (message) => {
  const pages = siteKnowledge.navigation.pages;
  
  for (const page of pages) {
    if (message.includes(page.name.toLowerCase()) || message.includes(page.path.replace('/', ''))) {
      return `You can visit the ${page.name} page at ${page.path} to ${page.description.toLowerCase()}. This page will help you ${getPageSpecificHelp(page.name)}`;
    }
  }
  
  return `Here are the main pages you can visit on this website:
  
🏠 **Home** - Main landing page with Francis Odame's story and services
👤 **About** - Learn about Francis Odame's background and mission  
📚 **Books** - View his publications and purchase options
🎤 **Speaking** - Explore his speaking services and book him
📝 **Blog** - Read articles on health and wellness topics
💬 **Testimonials** - See feedback from people he's worked with
📞 **Contact** - Get in touch with Francis Odame

Which page would you like to learn more about?`;
};

// Author query handler
const handleAuthorQuery = (message) => {
  const author = siteKnowledge.author;
  
  if (message.includes('mission') || message.includes('purpose')) {
    return `Francis Odame's mission is: "${author.mission}". His vision is: "${author.vision}". He focuses on inspiration, education, and connection through storytelling and practical health strategies.`;
  }
  
  if (message.includes('background') || message.includes('story') || message.includes('who') || message.includes('tell me about')) {
    return `Francis Odame is ${author.title}. ${author.background} His expertise includes ${author.expertise.join(', ')}. He's dedicated to healing through words and empowering communities through wellness advocacy.`;
  }
  
  return `Francis Odame is ${author.title}. ${author.description} He specializes in storytelling, practical health strategies, and engaging dialogue for communities, schools, and organizations. His mission is to inspire healthier, purpose-driven lives through the power of words and knowledge.`;
};

// Book query handler
const handleBookQuery = (message) => {
  const book = siteKnowledge.books.main;
  const platforms = siteKnowledge.books.platforms;
  
  if (message.includes('where') || message.includes('buy') || message.includes('purchase')) {
    let response = `You can purchase "${book.title}" from several platforms:\n\n`;
    platforms.forEach(platform => {
      response += `📚 **${platform.name}**: ${platform.description}\n`;
    });
    response += `\n🔗 **Main purchase link**: ${book.purchaseLink}`;
    return response;
  }
  
  if (message.includes('about') || message.includes('what') || message.includes('story')) {
    return `"${book.title}" is ${book.description}. The book explores themes of ${book.themes.join(', ')} and is available in ${book.genre} genre. You can purchase it from various platforms including Waterstones, Hatchards, Google Books, and Barnes & Noble.`;
  }
  
  return `Francis Odame's main book is "${book.title}" - ${book.description}. You can purchase it from various platforms including Waterstones, Hatchards, Google Books, Barnes & Noble, and more. The main purchase link is: ${book.purchaseLink}`;
};

// Speaking query handler
const handleSpeakingQuery = (message) => {
  const speaking = siteKnowledge.speaking;
  
  if (message.includes('book') || message.includes('hire') || message.includes('speak')) {
    return `You can book Francis Odame for speaking engagements! He offers:
    
🎤 **School Assemblies** - Interactive presentations for students
⛪ **Church Events** - Inspirational talks combining faith and wellness  
🏘️ **Community Workshops** - Hands-on workshops for community groups
🏢 **Corporate Events** - Professional wellness programs

**Contact him at:**
📞 Phone: ${speaking.booking.phone}
💬 WhatsApp: ${speaking.booking.whatsapp}
📧 Email: ${speaking.booking.email}

Visit the Contact page for detailed booking forms!`;
  }
  
  if (message.includes('topics') || message.includes('what') || message.includes('about')) {
    return `Francis Odame speaks on various topics including:
    
• ${speaking.topics.join('\n• ')}

His speaking style combines storytelling, practical health strategies, and engaging dialogue. He's available for school assemblies, church events, community workshops, and corporate wellness programs.`;
  }
  
  return `Francis Odame offers keynote speaking, workshops, and community outreach services. His topics include mental health awareness, resilience building, community wellness, and youth empowerment. You can book him through the Contact page or WhatsApp at ${speaking.booking.whatsapp}.`;
};

// Contact query handler
const handleContactQuery = (message) => {
  const contact = siteKnowledge.contact;
  
  return `You can contact Francis Odame through multiple channels:

📧 **Email**: ${contact.email}
📞 **Phone**: ${contact.phone}
💬 **WhatsApp**: ${contact.whatsapp}

**Social Media:**
🔗 Facebook: ${contact.social.facebook}
📸 Instagram: ${contact.social.instagram}
💼 LinkedIn: ${contact.social.linkedin}
🎵 TikTok: ${contact.social.tiktok}

You can also use the contact form on the Contact page to book speaking engagements or send detailed inquiries.`;
};

// Mission query handler
const handleMissionQuery = (message) => {
  const author = siteKnowledge.author;
  const values = siteKnowledge.values;
  
  return `Francis Odame's mission is: "${author.mission}"

His vision is: "${author.vision}"

**His core values include:**
✨ **Inspiration**: ${values.inspiration}
📚 **Education**: ${values.education}
🤝 **Connection**: ${values.connection}

He focuses on healing through words and empowering communities through wellness advocacy.`;
};

// Blog query handler
const handleBlogQuery = (message) => {
  return `Francis Odame's blog features articles and resources on health, wellness, and community topics. You can find his latest posts on the Blog page, covering topics like mental health awareness, resilience building, community wellness, and practical health strategies for everyday life.`;
};

// Testimonial query handler
const handleTestimonialQuery = (message) => {
  return `The Testimonials page contains feedback and reviews from people who have worked with Francis Odame or attended his speaking engagements. You can read about their experiences with his books, speaking services, and community outreach programs.`;
};

// Help query handler
const handleHelpQuery = () => {
  return `I can help you learn about Francis Odame and navigate this website! Here's what I can assist you with:

👤 **About Francis Odame** - His background, mission, and expertise
📚 **Books** - Information about "Because of Abigail" and where to purchase it
🎤 **Speaking Services** - His topics, booking process, and speaking style
🧭 **Navigation** - Help you find specific pages and content
📞 **Contact** - How to reach Francis Odame
💬 **General Questions** - About his work, values, and approach

What would you like to know more about?`;
};

// Helper functions
const getPageSpecificHelp = (pageName) => {
  const helpMap = {
    'Home': 'learn about Francis Odame\'s story and services',
    'About': 'discover his background and mission',
    'Books': 'view his publications and purchase options',
    'Speaking': 'explore his speaking services',
    'Blog': 'read his latest articles',
    'Testimonials': 'see feedback from people he\'s worked with',
    'Contact': 'get in touch with Francis Odame'
  };
  return helpMap[pageName] || 'find relevant information';
};

const isUnrelatedQuery = (message) => {
  const unrelatedKeywords = [
    'weather', 'news', 'sports', 'politics', 'other', 'unrelated',
    'cooking', 'travel', 'shopping', 'entertainment', 'technology',
    'science', 'history', 'math', 'music', 'movies'
  ];
  return unrelatedKeywords.some(keyword => message.includes(keyword));
};
